import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBriefcase, 
  faCalendarAlt, 
  faMapMarkerAlt, 
} from '@fortawesome/free-solid-svg-icons';

const ExperienceHero = ({ REF }) => {
  const experience = {
    role: "Full-Stack Developer Intern",
    company: "Green Energy Park.",
    period: "May 2025 - September 2025",
    location: "Benguerir",
    description: [
        "Designed and implemented the official website for Green Energy Park, ensuring a clean, responsive, and intuitive user experience using Next.js, Tailwind CSS, and Framer Motion, with supporting backend logic.",
        "Architected responsive frontend modules using Next.js and TypeScript.",
        "implemented an admin dashboard for managing research papers, news, and projects, improving content organization and accessibility.",
        "Optimized web application performance by addressing performance issues identified using Google Lighthouse."
    ]
  };

  return (
    <section ref={REF} className="font-poppins relative w-full min-h-screen flex items-center justify-center p-6 md:p-12 overflow-hidden isolate">
      {/* 2. Optimized Timeline Line */}
      <div className="absolute left-8 md:left-24 top-0 bottom-0 w-[1px] bg-slate-800/40">
        <motion.div 
          initial={{ height: 0 }}
          whileInView={{ height: '100%' }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          className="w-full bg-gradient-to-b from-transparent via-blue-500 to-transparent"
        />
      </div>

      <div className="relative z-10 w-full max-w-6xl flex flex-col gap-10">
        
        {/* ADDED TITLE HERE */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="ml-12"
        >
          <h2 className="text-xl md:text-3xl font-bold text-slate-300 tracking-[0.2em] uppercase">
            Experiences
          </h2>
        </motion.div>

        {/* 3. Performance-Optimized Glass Card */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ 
            willChange: "transform, opacity",
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden'
          }}
          className="ml-12 md:ml-32 w-full bg-slate-900/30 border border-white/10 rounded-[2.5rem] p-8 md:p-16 backdrop-blur-md relative"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold tracking-widest uppercase mb-8">
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>{experience.period}</span>
              </div>
              
              <h3 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                {experience.role}
              </h3>
              <p className="text-xl text-blue-400 font-medium mb-10 flex items-center gap-3">
                <FontAwesomeIcon icon={faBriefcase} className="text-slate-500" />
                {experience.company}
              </p>

              <div className="flex items-center gap-2 text-slate-400">
                <FontAwesomeIcon icon={faMapMarkerAlt} size="sm" />
                <span className="text-sm font-mono tracking-widest uppercase">{experience.location}</span>
              </div>
            </div>

            {/* Right Content - Achievement List */}
            <div className="relative">
              <div className="absolute -left-8 top-0 bottom-0 w-[1px] bg-white/5 hidden lg:block" />
              <div className="flex flex-wrap gap-4">
                {experience.description.map((item, i) => (
                    <motion.div 
                    key={i}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + (i * 0.1), duration: 0.4 }}
                    style={{ willChange: "transform, opacity" }}
                    className="group flex items-start gap-4 px-6 py-4 transition-all duration-300 shadow-sm"
                    >
                    <div className="relative flex mt-2 items-center justify-center shrink-0">
                        <div className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_10px_#10b981] group-hover:scale-125 transition-transform duration-300" />
                        <div className="absolute h-4 w-4 rounded-full border border-emerald-500/20 group-hover:scale-150 transition-transform duration-500" />
                    </div>

                    <span className="text-slate-200 text-sm md:text-base font-medium tracking-tight leading-relaxed">
                        {item}
                    </span>
                    </motion.div>
                ))}
                </div>
            </div>

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExperienceHero;